import {
    getOnlineBookingDaycareDayCount,
    getOnlineBookingItemPrice
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { LoyaltyEstimateItem } from 'types/Loyalty';
import { isOnlineBookingBoardingItem, OnlineBookingItem } from 'types/OnlineBooking';
import { getOnlineBookingAddonUnits } from 'utils/onlineBookingAddons';

/**
 * Maps the in-progress online booking items into Loyalty estimate line items,
 * using the same resolved amounts shown in the booking details:
 * - service/evaluation/grooming/daycare/boarding lines -> `service`
 * - add-ons -> `add_on`
 *
 * Daycare multi-day bookings multiply the service by the day count, and an
 * add-on is its price by its units (days and quantities), matching
 * `getOnlineBookingTotalAmount`.
 * Only positive amounts are sent; the engine decides which items actually earn
 * points.
 */
export const buildLoyaltyEstimateItems = (
    bookingItems: Array<OnlineBookingItem>
): Array<LoyaltyEstimateItem> => {
    const items: Array<LoyaltyEstimateItem> = [];

    bookingItems.forEach(bookingItem => {
        const dayCount = getOnlineBookingDaycareDayCount(bookingItem);
        const boardingNights = isOnlineBookingBoardingItem(bookingItem)
            ? bookingItem.endDate?.diff(bookingItem.startDate, 'days')
            : undefined;
        const nights = boardingNights || 1;

        const servicePrice = getOnlineBookingItemPrice(bookingItem);

        if (servicePrice > 0) {
            items.push({ type: 'service', amount: servicePrice * dayCount * nights });
        }

        bookingItem.addons.forEach(addon => {
            const amount = addon.price * getOnlineBookingAddonUnits(bookingItem, addon);

            if (amount > 0) {
                items.push({ type: 'add_on', amount });
            }
        });
    });

    return items;
};

/**
 * Builds the informational Loyalty Points message, pluralized:
 * "You'll earn 1 loyalty point" for exactly 1, otherwise "... N loyalty points".
 */
export const buildLoyaltyPointsMessage = (pointsToEarn: number): string => {
    const pointsLabel = pointsToEarn === 1 ? 'loyalty point' : 'loyalty points';

    return `You'll earn ${pointsToEarn} ${pointsLabel}`;
};
